import { Link } from "react-router-dom";
import FaqSchema from "@/components/FaqSchema";
import ResourceLayout, { AnswerBlock, DataTable } from "@/components/ResourceLayout";

const FAQS = [
  {
    q: "How do you calculate bulk recipe conversions?",
    a: "Divide the target yield by the source yield to get a conversion factor. Multiply every ingredient weight and volume by that factor. Round to a practical portion, recheck seasoning at the new scale, and retest cooking time. Baking soda, salt, and leavening agents rarely scale in perfect linear fashion.",
  },
  {
    q: "What is the difference between weight and volume conversion?",
    a: "Weight measures mass and is constant. Volume measures the space an ingredient occupies and changes with density, sift, and packing. Flour weighs about 120 grams per cup unsifted and 100 grams sifted. Professional kitchens standardise on weight so a recipe reads the same on any station.",
  },
  {
    q: "How do I convert cups to grams for baking?",
    a: "Use an ingredient density chart. One cup of all-purpose flour is roughly 125 grams, one cup of granulated sugar is 200 grams, and one cup of butter is 227 grams. Always weigh butter, sugar, and flour on a scale rather than rely on a cup measure once you scale above a home portion.",
  },
];

export default function BulkConversion() {
  return (
    <ResourceLayout
      activeSlug="bulk-conversion"
      title="Bulk Conversion: how to scale a recipe without losing the plate"
      lede="Density charts, weight-to-volume conversions, and the practical rules for scaling a recipe from six covers to six hundred. Includes standard container yields, master conversion factors, and the salt and leavening rules that break at scale."
    >
      <FaqSchema
        id="bulk-conversion"
        title="Bulk Recipe Conversion Charts and Formulas | Culinaire Specialist"
        description="Weight to volume conversions, ingredient density charts, and scaling factors for converting recipes from home portions to bulk production."
        canonicalUrl="https://www.culinairespecialist.com/resources/bulk-conversion"
        faqs={FAQS}
      />

      <AnswerBlock
        id="what-is-bulk-conversion"
        heading="What is bulk recipe conversion?"
        answer="Bulk recipe conversion is the process of scaling a written recipe up or down while keeping flavour, texture, and yield consistent. It starts with a conversion factor: target yield divided by source yield. Every ingredient scales by that factor. Salt, leavening, and cooking time need manual re-checks."
        softwareNote={<>Culinaire Specialist scales any recipe in one input. Enter the target number of covers, every ingredient recomputes to weight, and the plate cost auto-updates. <Link to="/register" className="gold underline">Start free</Link>.</>}
      />

      <AnswerBlock
        id="how-to-calculate-bulk-recipe-conversions"
        heading="How to calculate bulk recipe conversions"
        answer="Take the target yield and divide by the source yield. That number is your conversion factor. Multiply every ingredient by that factor, then round to a practical portion so you are not weighing 1.13 grams of nutmeg. Retest the recipe once at the new scale to catch salt, spice, and time issues."
      >
        <DataTable
          caption="Conversion factor examples"
          columns={["Source yield", "Target yield", "Factor", "Note"]}
          rows={[
            ["Serves 4", "Serves 24", "6.00", "Standard banquet scaling"],
            ["Serves 4", "Serves 100", "25.00", "Recheck leavening and salt"],
            ["Serves 6", "Serves 60", "10.00", "Watch reduction times"],
            ["1 loaf (800 g)", "40 loaves (32 kg)", "40.00", "Baker's percentage handles this cleanly"],
            ["2 L stock", "20 L stock", "10.00", "Cooking time does not scale linearly"],
            ["Serves 12", "Serves 8", "0.667", "Round to nearest kitchen unit"],
          ]}
          testId="bulk-conversion-table-factor"
        />
      </AnswerBlock>

      <AnswerBlock
        id="density-chart"
        heading="Ingredient density chart: cups to grams"
        answer="Volume measures depend on how tightly an ingredient packs. Weighing is always more reliable at production scale. The chart below lists the widely-accepted approximate weights for one US measuring cup. Use these when converting a home recipe that lists cups into a professional prep sheet that lists grams."
      >
        <DataTable
          caption="Approximate weight per US cup (240 ml)"
          columns={["Ingredient", "Weight per cup", "Notes"]}
          rows={[
            ["All-purpose flour (unsifted)", "125 g", "Spoon and level"],
            ["All-purpose flour (sifted)", "100 g", "After sifting"],
            ["Bread flour", "130 g", "Higher protein content"],
            ["Cake flour", "115 g", "Lower protein, softer"],
            ["Granulated sugar", "200 g", ""],
            ["Brown sugar (packed)", "220 g", "Press firmly"],
            ["Icing sugar (unsifted)", "120 g", "Fluff first"],
            ["Butter", "227 g", "Two US sticks"],
            ["Milk", "240 g", "Same as water at 1 g/ml"],
            ["Heavy cream", "232 g", ""],
            ["Cocoa powder", "85 g", "Sift for accurate weight"],
            ["Rolled oats", "90 g", ""],
            ["Rice (raw, long grain)", "185 g", ""],
            ["Salt (kosher, Diamond)", "142 g", "Morton is heavier at 273 g"],
            ["Honey", "340 g", "Warm slightly to pour"],
            ["Olive oil", "216 g", ""],
          ]}
          testId="bulk-conversion-table-density"
        />
      </AnswerBlock>

      <AnswerBlock
        id="unit-conversions"
        heading="Metric, imperial, and hospitality unit conversions"
        answer="Every professional kitchen keeps a laminated card near the pass with the core unit conversions. Learning ten of them by heart removes almost every conversion error at service. The table below covers weight, volume, temperature, and a few hospitality-specific measures like fluid ounces and pinches."
      >
        <DataTable
          caption="Core kitchen unit conversions"
          columns={["From", "To", "Multiply by"]}
          rows={[
            ["1 kilogram", "Pounds", "2.20462"],
            ["1 pound", "Grams", "453.592"],
            ["1 ounce (weight)", "Grams", "28.3495"],
            ["1 litre", "US fluid ounces", "33.814"],
            ["1 US cup", "Millilitres", "236.588"],
            ["1 tablespoon (Aus 20 ml)", "Teaspoons", "4 (Aus) or 3 (US)"],
            ["1 US fluid ounce", "Millilitres", "29.574"],
            ["1 imperial pint", "Millilitres", "568.261"],
            ["Celsius to Fahrenheit", "(°C × 9/5) + 32", ""],
            ["Fahrenheit to Celsius", "(°F minus 32) × 5/9", ""],
            ["Pinch (kitchen)", "Grams", "≈ 0.36 g"],
            ["Dash (liquid)", "Millilitres", "≈ 0.9 ml"],
          ]}
          testId="bulk-conversion-table-units"
        />
      </AnswerBlock>

      <AnswerBlock
        id="pan-and-container-yields"
        heading="Pan sizes, hotel pans, and container yields"
        answer="Scaling a recipe often means choosing a bigger pan. A standard 1/1 full-size gastronorm hotel pan holds about 21 litres to the rim and 14 litres to a working depth. Round cake tins do not scale linearly by radius; a 20 cm tin holds 1.4 L while a 24 cm tin holds 2.0 L, forty percent more capacity."
      >
        <DataTable
          caption="Common pan and container yields"
          columns={["Vessel", "Working volume", "Note"]}
          rows={[
            ["Gastronorm 1/1 × 65 mm deep", "≈ 9 L", "Standard buffet pan"],
            ["Gastronorm 1/1 × 100 mm deep", "≈ 14 L", "Most common depth"],
            ["Gastronorm 1/1 × 150 mm deep", "≈ 21 L", "Braising, stock"],
            ["Gastronorm 1/2 × 65 mm deep", "≈ 4.3 L", ""],
            ["Round cake tin 20 cm", "≈ 1.4 L batter", "For 6 cm high cake"],
            ["Round cake tin 24 cm", "≈ 2.0 L batter", ""],
            ["Loaf tin 22 × 11 × 7 cm", "≈ 1.5 L", "One loaf 900 g bread dough"],
            ["Half sheet pan", "≈ 45 × 33 cm", "Fits domestic ovens"],
            ["Full sheet pan", "≈ 66 × 46 cm", "Commercial ovens only"],
            ["12-cup muffin tin", "≈ 100 ml per cup", "Fill 3/4 to allow rise"],
          ]}
          testId="bulk-conversion-table-pans"
        />
      </AnswerBlock>

      <AnswerBlock
        id="what-does-not-scale"
        heading="What does not scale linearly when you go bulk"
        answer="Salt, chilli, and highly volatile aromatics scale sub-linearly. A recipe that calls for two grams of salt for four covers will taste over seasoned at fifty grams for one hundred covers. Cooking time also scales non-linearly: a two litre stock takes an hour, a twenty litre stock takes ninety minutes."
        softwareNote={<>The Digital Corporate Chef inside Culinaire Specialist automatically flags when a scaled recipe crosses an inflection point on salt, leavening, or reduction time, so bulk production never surprises the pass.</>}
      />
    </ResourceLayout>
  );
}
